import { get_domain_style } from '@/main';
import { navbar_core_methods } from './navbar_core_methods';
import { navbar_platform_methods } from './navbar_platform_methods';
import { navbar_profile_methods } from './navbar_profile_methods';
import { navbar_search_branding_methods } from './navbar_search_branding_methods';
import { navbar_notification_methods } from './navbar_notification_methods';
import { navbar_chat_notification_methods } from './navbar_chat_notification_methods';

export default {
  name: 'NavbarView',

  data: () => ({
    user: {},
    form: {},
    original_user: null,
    original_company: null,
    original_profile: null,
    saving: false,
    user_profile: {
      phone_number: '',
      address: '',
      id_number: '',
      province: '',
      country: '',
      gender: ''
    },
    new_company: {
      company_name: '',
      registration_number: '',
      vat_number: '',
      phone_number: '',
      address: ''
    },
    user_initials: '',
    edit_mode: false,
    email_touched: false,
    email_error: '',
    registration_error: '',
    vat_error: '',
    id_number_error: null,
    search_query: '',
    show_panel: false,
    is_searching: false,
    results: [],
    total_count: 0,
    limit: 10,
    offset: 0,
    debounce_id: null,
    search_modal: null,
    _search_modal_visible: false,
    logo_url: null,

    // NOTIFICATION & MESSAGES PROPERTIES
    notifications: [],
    unread_count: 0,
    loading_notifications: false,
    notification_filter: 'all',
    notification_poll_interval: null,
    last_notification_fetch: null,
    unread_chat_count: 0,
    chat_socket: null,
    chat_socket_connected: false,
    chat_socket_last_error: null,
    allowed_app_keys: [],
    platform_access_loading: false,
    launching_platform: false,
    is_internal_suite: false,
    drive_mobile_nav_mode: 'menu',
    drive_mobile_nav_title: 'My Drive',

    // BROWSER NOTIFICATION FLAGS
    _notifications_initialized: false,
    _chat_count_initialized: false,
  }),

  computed: {
    styleConfig() {
      return get_domain_style();
    },
    is_email_valid() {
      const email = (this.user?.email || '').trim();
      if (!email) return false;
      const re = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i;
      return re.test(email);
    },
    grouped_results() {
      return this.build_grouped_search_results();
    },

    filtered_notifications() {
      if (this.notification_filter === 'unread') {
        return this.notifications.filter(n => n.status !== 'READ');
      }
      return this.notifications;
    },

    is_dashboard_route() {
      return ['IndividualDashboard', 'Home'].includes(this.$route?.name);
    },

    is_drive_route() {
      return this.$route?.name === 'Drive';
    },

    drive_mobile_nav_is_back() {
      return this.drive_mobile_nav_mode === 'back';
    },

    mobile_first_name() {
      return String(
        Reflect.get(this.user ?? {}, 'first_name') ?? ''
      ).trim().split(/\s+/)[0];
    },

    mobile_has_long_name() {
      return this.mobile_first_name.length >= 10;
    },

    mobile_dashboard_greeting() {
      const hour = new Date().getHours();

      if (hour < 12) {
        return 'Good morning';
      }

      if (hour < 17) {
        return 'Good afternoon';
      }

      return 'Good evening';
    },

    user_display_name() {
      const first = String(this.user?.first_name).trim();
      const last = String(this.user?.last_name).trim();
      return `${first} ${last}`.trim()|| 'User';
    },

    user_email() {
      return this.user?.email || '';
    },

    user_role_label() {
      return this.user?.role || localStorage.getItem('role') || '';
    },

    can_access_settings() {
      return this.is_admin();
    },

    mobile_nav_title() {
      const title_map = {
        Create: 'Workspaces',
        Chat: 'Chat',
        AuditTrail: 'Activity',
        Ziva: 'Ziva Assistant',
        MainSettings: 'Settings',
        Settings: 'Users',
        Profile: 'Profile',
        Permission: 'Permissions',
        Role: 'Roles',
        RolePermissions: 'Role Permissions',
        AccessControl: 'Access Control',
        SystemConfigure: 'System Configure',
        UserPermissions: 'User Permissions',
        SupersetManager: 'Manage Superset',
        PlatformManagement: 'Platform Management',
        AddPlatformConfig: 'Platform Configuration',
      };

      return [
        this.is_drive_route && this.drive_mobile_nav_title,
        Reflect.get(title_map, this.$route?.name),
        this.$route?.meta?.title,
        'AIC One',
      ].find(Boolean);
    },

    mobile_today_label() {
      return new Intl.DateTimeFormat('en-ZA', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      }).format(new Date());
    }
  },

  mounted() {
    this.mount_navbar();
  },

  beforeUnmount() {
    this.unmount_navbar();
  },

  methods: {
    ...navbar_core_methods,
    ...navbar_platform_methods,
    ...navbar_profile_methods,
    ...navbar_search_branding_methods,
    ...navbar_notification_methods,
    ...navbar_chat_notification_methods,
  },

  watch: {
    '$branding.logos.logo': {
      handler(new_logo) {
        if (new_logo) {
          this.logo_url = new_logo;
        }
      },
      deep: true
    },
    '$branding.logos.favicon': {
      handler(new_favicon) {
        if (new_favicon) {
          this.$branding.update_favicon(new_favicon);
        }
      },
      deep: true
    }
  }
};
